import type { SourceOrganization } from '@/services/migration/migrationService';

export interface SourceOrgTreeNode {
  org: SourceOrganization;
  children: SourceOrgTreeNode[];
}

function compareName(a: SourceOrganization, b: SourceOrganization): number {
  return a.o_name.localeCompare(b.o_name, 'sv');
}

function nearestParentId(
  org: SourceOrganization,
  byId: Map<number, SourceOrganization>
): number | null {
  if (org.o_pid != null && org.o_pid !== org.o_id && byId.has(org.o_pid)) {
    return org.o_pid;
  }
  if (!org.o_path) return null;
  const parts = org.o_path
    .split(',')
    .map(part => Number(part.trim()))
    .filter(id => Number.isFinite(id));
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const id = parts[i];
    if (id === org.o_id) continue;
    if (byId.has(id)) return id;
  }
  return null;
}

function parentCreatesCycle(
  orgId: number,
  parentId: number,
  parentOf: Map<number, number>
): boolean {
  const seen = new Set<number>([orgId]);
  let current: number | undefined = parentId;
  while (current != null) {
    if (seen.has(current)) return true;
    seen.add(current);
    current = parentOf.get(current);
  }
  return false;
}

export function buildSourceOrgTree(orgs: SourceOrganization[]): SourceOrgTreeNode[] {
  const byId = new Map(orgs.map(org => [org.o_id, org]));
  const parentOf = new Map<number, number>();

  for (const org of orgs) {
    const parentId = nearestParentId(org, byId);
    if (parentId == null) continue;
    if (parentCreatesCycle(org.o_id, parentId, parentOf)) continue;
    parentOf.set(org.o_id, parentId);
  }

  const childIds = new Map<number, number[]>();
  const rootIds: number[] = [];
  for (const org of orgs) {
    const parentId = parentOf.get(org.o_id);
    if (parentId == null) {
      rootIds.push(org.o_id);
      continue;
    }
    const list = childIds.get(parentId) ?? [];
    list.push(org.o_id);
    childIds.set(parentId, list);
  }

  const build = (id: number): SourceOrgTreeNode | null => {
    const org = byId.get(id);
    if (!org) return null;
    const children = (childIds.get(id) ?? [])
      .map(childId => build(childId))
      .filter((node): node is SourceOrgTreeNode => node != null)
      .sort((a, b) => compareName(a.org, b.org));
    return { org, children };
  };

  const roots = rootIds
    .map(id => build(id))
    .filter((node): node is SourceOrgTreeNode => node != null);

  roots.sort((a, b) => {
    const aTop = a.org.o_topnode === 1 ? 0 : 1;
    const bTop = b.org.o_topnode === 1 ? 0 : 1;
    if (aTop !== bTop) return aTop - bTop;
    return compareName(a.org, b.org);
  });

  return roots;
}

function matchesQuery(org: SourceOrganization, query: string): boolean {
  return org.o_name.toLowerCase().includes(query) || String(org.o_id).includes(query);
}

export function filterSourceOrgTree(
  nodes: SourceOrgTreeNode[],
  query: string
): SourceOrgTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;
  const out: SourceOrgTreeNode[] = [];
  for (const node of nodes) {
    if (matchesQuery(node.org, q)) {
      out.push(node);
      continue;
    }
    const children = filterSourceOrgTree(node.children, q);
    if (children.length > 0) {
      out.push({ org: node.org, children });
    }
  }
  return out;
}

export function flattenSourceOrgTree(nodes: SourceOrgTreeNode[]): SourceOrganization[] {
  const out: SourceOrganization[] = [];
  const walk = (list: SourceOrgTreeNode[]) => {
    for (const node of list) {
      out.push(node.org);
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

export function collectExpandableIds(nodes: SourceOrgTreeNode[]): number[] {
  const ids: number[] = [];
  const walk = (list: SourceOrgTreeNode[]) => {
    for (const node of list) {
      if (node.children.length > 0) {
        ids.push(node.org.o_id);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

export function countDescendants(node: SourceOrgTreeNode): number {
  return node.children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}
